import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Imperative confirm() replacement. Usage:
//   const ok = await confirmDialog({ title, description, confirmLabel, destructive });
//   if (!ok) return;
//
// Mount <ConfirmDialogProvider> once at the root of the app, then call
// `confirmDialog(...)` from anywhere — same ergonomics as `window.confirm()`.

const ConfirmCtx = createContext(null);
const externalHandle = { fn: null };

export function confirmDialog(opts) {
  if (externalHandle.fn == null) {
    return Promise.resolve(window.confirm(opts.description || opts.title || "Are you sure?"));
  }
  return externalHandle.fn(opts);
}

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const request = useCallback((options) => {
    setOpts(options || {});
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  useEffect(() => {
    externalHandle.fn = request;
    return () => { externalHandle.fn = null; };
  }, [request]);

  const close = useCallback((result) => {
    setOpen(false);
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setTimeout(() => resolver?.(result), 0);
  }, []);

  const {
    title = "Are you sure?",
    description = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = false,
  } = opts;

  return (
    <ConfirmCtx.Provider value={request}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent
          className="bg-cream border border-navy/15"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-navy">{title}</AlertDialogTitle>
            {description && (
              <AlertDialogDescription className="text-navy/70 text-sm font-light leading-relaxed">
                {description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => close(false)}
              data-testid="confirm-dialog-cancel"
              className="border border-navy/30 text-navy text-xs uppercase tracking-widest hover:bg-navy hover:text-gold"
            >
              {cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              data-testid="confirm-dialog-confirm"
              className={`text-xs uppercase tracking-widest ${
                destructive
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-navy text-gold hover:bg-navy-600"
              }`}
            >
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  return ctx;
}
